import {useRef, useMemo} from "react"
import {useFrame} from "@react-three/fiber"
import * as THREE from "three"
import React, {useState, useEffect, useCallback} from "react";
import {LHQInstance} from "../../LHQ/Scene.js";
import {useGLTF_fixed} from "../../../../../Utils/ThreeJS/3JSHooks.js";
import {Vec3} from "../../../../../Utils/ThreeJS/VectorStructs.js";

export const CoinCatcherSet = ((props: {lhq: LHQInstance})=>{
	return <CoinCatcher coinSpawnRate={1.5}/>;
});

export function Bank(props: {extraProcessing_late?: (mesh: THREE.Mesh)=>void}) {
	const {extraProcessing_late} = props;
	const {scene} = useGLTF_fixed("/3D/ZUp/Bank_2.0.glb");

	const [objectsFinal, setObjectsFinal] = useState<THREE.Mesh[]>([]);
	useEffect(()=>{
		if (scene == null) return;
		const newObjectsFinal = [] as THREE.Mesh[];
		scene.traverse(node=>{
			if (node["isMesh"]) {
				const mesh = node as THREE.Mesh;
				//console.log(`Bank mesh(${mesh.name}):`, mesh);
				extraProcessing_late?.(mesh);
				newObjectsFinal.push(mesh);
			}
		});
		setObjectsFinal(newObjectsFinal);
	}, [extraProcessing_late, scene]);

	if (objectsFinal.length == 0) return null;
	return (
		<group position={new Vec3(0, 0, 0).To3JPos()} rotation={[0, 0, 0]}>
			{objectsFinal.map((obj, i)=><primitive key={i} object={obj}/>)}
		</group>
	);
}

export function Coin(props: {extraProcessing_late?: (mesh: THREE.Mesh)=>void}) {
	const {extraProcessing_late} = props;
	const {scene} = useGLTF_fixed("/3D/ZUp/Coin_2.0.glb");

	const [objectsFinal, setObjectsFinal] = useState<THREE.Mesh[]>([]);
	useEffect(()=>{
		if (scene == null) return;
		const newObjectsFinal = [] as THREE.Mesh[];
		scene.traverse(node=>{
			if (node["isMesh"]) {
				const mesh = node as THREE.Mesh;

				//console.log(`Coin mesh(${mesh.name}):`, mesh);
				mesh.visible = false; // Hide coin initially
				mesh.scale.set(5, 5, 5); // Scale up the coin

				// change material to MeshBasicMaterial (keep texture and color assigned to old material though)
				//console.log(mesh.material); // NOTE: LOGGING MATERIALS IS SLOW!
				const oldMaterial = mesh.material as THREE.MeshStandardMaterial;
				mesh.material = new THREE.MeshBasicMaterial({
					map: oldMaterial.map,
					color: oldMaterial.color,
					transparent: oldMaterial.transparent,
					opacity: oldMaterial.opacity,
					depthWrite: oldMaterial.depthWrite,
					depthTest: oldMaterial.depthTest,
					side: oldMaterial.side
				});

				extraProcessing_late?.(mesh);
				newObjectsFinal.push(mesh);
			}
		});
		setObjectsFinal(newObjectsFinal);
	}, [extraProcessing_late, scene]);

	if (objectsFinal.length == 0) return null;
	return (
		<group position={new Vec3(0, 0, 0).To3JPos()} rotation={[0, 0, 0]}>
			{objectsFinal.map((obj, i)=><primitive key={i} object={obj}/>)}
		</group>
	);
}

interface CoinCatcherProps {
	roomSize?: number;
	maxPairs?: number; // Total pairs available (more than active to allow storage)
	activePairs?: number; // How many coin/piggy bank pairs active at once
	coinSpawnRate?: number; // Seconds between new coin spawns
}

export function CoinCatcher({
	roomSize = 6,
	maxPairs = 6, // Allow more storage slots
	activePairs = 4, // Keep X active at once
	coinSpawnRate = 0.5
}: CoinCatcherProps) {
	const groupRef = useRef<THREE.Group>(null);

	// Play coin clink sound
	const playClink = ()=>{
		if (!audioContext.current) {
			audioContext.current = new (window.AudioContext || (window as any).webkitAudioContext)();
		}

		const oscillator = audioContext.current.createOscillator();
		const gainNode = audioContext.current.createGain();

		oscillator.connect(gainNode);
		gainNode.connect(audioContext.current.destination);

		oscillator.frequency.setValueAtTime(800, audioContext.current.currentTime);
		oscillator.frequency.exponentialRampToValueAtTime(400, audioContext.current.currentTime + 0.1);

		gainNode.gain.setValueAtTime(0.3, audioContext.current.currentTime);
		gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.current.currentTime + 0.1);

		oscillator.start();
		oscillator.stop(audioContext.current.currentTime + 0.1);
	};

	// Update the data structures
	const pairs = useRef<Array<{
		active: boolean;
		currentFrame: number;
		catchFrame: number;
		history: Array<{
			bankPosition: THREE.Vector3;
			bankRotation: number;
			coinPosition: THREE.Vector3;
			coinRotation: number;
		}>;
		coinMesh: THREE.Mesh | null;
		bankMesh: THREE.Group | null;
	}>>([]);

	const spawnTimer = useRef(0);
	const audioContext = useRef<AudioContext | null>(null);
	const FPS = 60;
	const FRAME_TIME = 1 / FPS;
	const MAX_DURATION = 4; // seconds - longest possible duration
	const MAX_HISTORY_LENGTH = MAX_DURATION * FPS;

	// Initialize arrays
	useMemo(()=>{
		pairs.current = Array.from({length: maxPairs}, ()=>({
		active: false,
		currentFrame: 0,
		catchFrame: 0,
		history: [],
		coinMesh: null,
		bankMesh: null
		}));
	}, [maxPairs]);

	// Modified spawnPair function
	const spawnPair = ()=>{
		const pairIndex = pairs.current.findIndex(p=>!p.active);
		if (pairIndex === -1) return;

		const pair = pairs.current[pairIndex];

		console.log(`=== Spawning pair ${pairIndex} ===`);

		// === STEP 1: Calculate random parameters ===
		const catchLocation = new THREE.Vector3(
			(Math.random() - 0.5) * 1,
			(Math.random() - 0.5) * 1,
			0.5 // z-up: catch height is on z (vertical = z)
		);

		const coinTravelAngle = Math.random() * Math.PI * 2;
		const bankTravelAngle = Math.random() * Math.PI * 2;
		const catchTime = 1.5 + Math.random() * 0.5; // 1.5-2 seconds
		const catchFrame = Math.round(catchTime * FPS);

		// Duration before and after catch
		const preCatchTime = 1.0 + Math.random() * 0.5;
		const postCatchTime = 0.4 + Math.random() * 0.3;
		const preCatchFrames = Math.round(preCatchTime * FPS);
		const postCatchFrames = Math.round(postCatchTime * FPS);

		const totalFrames = preCatchFrames + 1 + postCatchFrames; // +1 for catch frame
		const actualCatchFrame = preCatchFrames; // Catch happens at this index

		console.log(`Catch at frame ${actualCatchFrame} of ${totalFrames} total frames`);

		// Initialize history array
		pair.history = new Array(totalFrames);
		pair.catchFrame = actualCatchFrame;

		// === STEP 2: Set catch point (both objects at same location and rotation) ===

		const catchRotation = Math.random() * Math.PI * 2; // Same rotation for both
		console.log(`Catch point: pos(${catchLocation.x.toFixed(2)}, ${catchLocation.y.toFixed(2)}, ${catchLocation.z.toFixed(2)}), rot(${catchRotation.toFixed(3)})`);
		/*pair.history[actualCatchFrame] = {
			bankPosition: catchLocation.clone(),
			bankRotation: catchRotation,
			coinPosition: catchLocation.clone(),
			coinRotation: catchRotation
		};*/

		// === STEP 3: Calculate constant velocities ===

		//const radius = roomSize / 2;

		// Coin travels in straight line (before gravity)
		const coinSpeed = 2.0 + Math.random() * 1.0;
		const coinDirection = new THREE.Vector3(
			Math.cos(coinTravelAngle),
			0,
			Math.sin(coinTravelAngle)
		);
		const coinPositionDeltaPerFrame = coinDirection.clone().multiplyScalar(coinSpeed * FRAME_TIME);

		// Bank travels in straight line
		const bankSpeed = 1.5 + Math.random() * 1.0;
		const bankDirection = new THREE.Vector3(
			Math.cos(bankTravelAngle),
			0,
			Math.sin(bankTravelAngle)
		);
		const bankPositionDeltaPerFrame = bankDirection.clone().multiplyScalar(bankSpeed * FRAME_TIME);

		// Different rotation speeds for bank and coin
		const coinRotationSpeed = (Math.random() - 0.5) * 6; // -3 to +3 radians/second
		const bankRotationSpeed = (Math.random() - 0.5) * 4; // -2 to +2 radians/second
		const coinRotationDeltaPerFrame = coinRotationSpeed * FRAME_TIME;
		const bankRotationDeltaPerFrame = bankRotationSpeed * FRAME_TIME;

		console.log(`Coin speed: ${coinSpeed.toFixed(2)}, Bank speed: ${bankSpeed.toFixed(2)} @coin_perFrame(${JSON.stringify(coinPositionDeltaPerFrame)}) @bank_perFrame(${JSON.stringify(bankPositionDeltaPerFrame)})`);
		console.log(`Coin rotation: ${coinRotationSpeed.toFixed(3)} rad/s, Bank rotation: ${bankRotationSpeed.toFixed(3)} rad/s @coin_perFrame(${coinRotationDeltaPerFrame.toFixed(3)}) @bank_perFrame(${bankRotationDeltaPerFrame.toFixed(3)})`);

		// === STEP 3.5: Calculate backward from catch-point, to find initial positions/rotations ===

		// Calculate the initial coin position and velocity for proper physics
		const totalCatchTime = actualCatchFrame * FRAME_TIME;

		// Calculate horizontal starting position (moving backward along travel path)
		const coinInitialPos = catchLocation.clone().sub(coinPositionDeltaPerFrame.clone().multiplyScalar(actualCatchFrame));

		// Calculate required initial height to reach catch point
		// Using: y_final = y_initial + v0*t - 0.5*g*t^2
		// Solving for y_initial when y_final = catchLocation.y
		const initialVelocityY = 3 + Math.random() * 2; // Random upward velocity (3-5 m/s)
		// z-up: gravity acts along -z, so the vertical physics run on z
		const requiredInitialHeight = catchLocation.z - (initialVelocityY * totalCatchTime) + (0.5 * 9.8 * totalCatchTime * totalCatchTime);
		coinInitialPos.z = requiredInitialHeight;

		console.log(`Coin initial position: (${coinInitialPos.x.toFixed(2)}, ${coinInitialPos.y.toFixed(2)}, ${coinInitialPos.z.toFixed(2)})`);
		console.log(`Coin initial Y velocity: ${initialVelocityY.toFixed(2)}, will reach catch height ${catchLocation.y.toFixed(2)}`);

		const bankInitialPos = catchLocation.clone().sub(bankPositionDeltaPerFrame.clone().multiplyScalar(actualCatchFrame));
		bankInitialPos.z = 0.2; // z-up: bank vertical start is on z
		console.log(`Bank initial position: (${bankInitialPos.x.toFixed(2)}, ${bankInitialPos.y.toFixed(2)}, ${bankInitialPos.z.toFixed(2)})`);

		// Set initial rotations
		const initialBankRotation = catchRotation - (bankRotationDeltaPerFrame * actualCatchFrame);
		const initialCoinRotation = catchRotation - (coinRotationDeltaPerFrame * actualCatchFrame);

		// === STEP 4: Extrapolate forward from initial positions/rotations ===

		for (let frame = 0; frame < totalFrames; frame++) {
			const timeFromStart = frame * FRAME_TIME;

			const historyEntry = {
				bankPosition: new THREE.Vector3(),
				bankRotation: 0,
				coinPosition: new THREE.Vector3(),
				coinRotation: 0
			};

			// Bank position (straight line backward from catch point)
			historyEntry.bankPosition.copy(bankInitialPos);
			historyEntry.bankPosition.add(bankPositionDeltaPerFrame.clone().multiplyScalar(frame));
			// z-up: vertical is z
			historyEntry.bankPosition.z = 0.2;

			// Coin position - simulate forward physics from initial position
			historyEntry.coinPosition.copy(coinInitialPos);
			historyEntry.coinPosition.add(coinPositionDeltaPerFrame.clone().multiplyScalar(frame));
			// Apply gravity: y = y0 + v0*t - 0.5*g*t^2
			// z-up: gravity along -z:
			historyEntry.coinPosition.z = coinInitialPos.z + (initialVelocityY * timeFromStart) - (0.5 * 9.8 * timeFromStart * timeFromStart);

			// Rotations
			historyEntry.bankRotation = initialBankRotation + (bankRotationDeltaPerFrame * frame);
			historyEntry.coinRotation = initialCoinRotation + (coinRotationDeltaPerFrame * frame);

			pair.history[frame] = historyEntry;
		}

		pair.active = true;
		pair.currentFrame = 0;

		// Make meshes visible
		if (pair.coinMesh) pair.coinMesh.visible = true;
		if (pair.bankMesh) pair.bankMesh.visible = true;

		console.log(`Baked ${pair.history.length} frames with catch at frame ${actualCatchFrame}:`, pair.history);
		console.log(`Verification - Frame 0: bank(${pair.history[0].bankRotation.toFixed(3)}), coin(${pair.history[0].coinRotation.toFixed(3)})`);
		console.log(`Verification - Catch frame: bank(${pair.history[actualCatchFrame].bankRotation.toFixed(3)}), coin(${pair.history[actualCatchFrame].coinRotation.toFixed(3)})`);
		console.log(`Verification - Last frame: bank(${pair.history[totalFrames-1].bankRotation.toFixed(3)}), coin(${pair.history[totalFrames-1].coinRotation.toFixed(3)})`);
	};

	// Update the useFrame logic
	useFrame((state, delta)=>{
		if (g.pauseRCE) return;
		spawnTimer.current += delta;

		// Count currently active pairs
		const currentlyActive = pairs.current.filter(p=>p.active).length;

		// Spawn new pairs if needed
		if (spawnTimer.current >= coinSpawnRate && currentlyActive < activePairs) {
			spawnTimer.current = 0;
			spawnPair();
		}

		// Update all active pairs
		pairs.current.forEach((pair, i)=>{
			if (!pair.active) return;

			// Advance frame
			pair.currentFrame++;

			// Check if pair is complete
			if (pair.currentFrame >= pair.history.length) {
				console.log(`Pair ${i} completed journey`);
				pair.active = false;
				if (pair.coinMesh) pair.coinMesh.visible = false;
				if (pair.bankMesh) pair.bankMesh.visible = false;
				return;
			}

			// Get current frame data
			const frameData = pair.history[pair.currentFrame];

			// Apply transforms
			if (pair.coinMesh) {
				// Hide coin after catch
				if (pair.currentFrame > pair.catchFrame) {
					if (pair.currentFrame === pair.catchFrame + 1) {
						console.log(`Pair ${i} - coin caught! Playing sound.`);
						playClink();
						pair.coinMesh.visible = false;
					}
				} else {
					pair.coinMesh.position.copy(frameData.coinPosition);
					pair.coinMesh.rotation.z = frameData.coinRotation; // z-up: spin about vertical = rotation.z
					/*pair.coinMesh.rotation.x = frameData.coinRotation;
					pair.coinMesh.rotation.z = frameData.coinRotation * 1.5;*/
				}
			}

			if (pair.bankMesh) {
				pair.bankMesh.position.copy(frameData.bankPosition);
				pair.bankMesh.rotation.z = frameData.bankRotation; // z-up: spin about vertical = rotation.z
			}
		});
	});

	return (
		<group ref={groupRef}>
			{/* Coins */}
			{pairs.current.map((_, i)=>{
				return <Coin key={`coin-${i}`} extraProcessing_late={useCallback(mesh=>{
					if (mesh) pairs.current[i].coinMesh = mesh;
				}, [i])}/>;
			})}

			{/* Piggy Banks */}
			{pairs.current.map((_, i)=>(
				<group key={`bank-${i}`}
					ref={ref=>{
						if (ref) pairs.current[i].bankMesh = ref;
					}}
					visible={false}
				>
					<Bank/>
				</group>
			))}

			{/* Room boundary */}
			{/*<mesh>
				<boxGeometry args={[roomSize, 0.1, roomSize]} />
				<meshBasicMaterial color="#E0E0E0" transparent opacity={0.3} />
			</mesh>*/}
		</group>
	);
}